import { Alert, CircularProgress } from "@mui/material";
import React, { Suspense, useContext, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Navigate, Route, Routes, useLocation, useParams } from "react-router-dom";

import { AuthContext } from "~/auth/AuthProvider";
import { isWhatsNewUnsubscribePath, WHATS_NEW_UNSUBSCRIBE_PATH } from "~/auth/publicPaths";
import RequireAuth from "~/auth/RequireAuth";
import usePortalAuth from "~/auth/usePortalAuth";
import StandaloneLaunchGate from "~/components/pwa/StandaloneLaunchGate";
import RequireSite from "~/components/RequireSite";
import RetryableErrorState from "~/components/RetryableErrorState";
import SentryErrorBoundary from "~/helpers/SentryErrorBoundary";
import useErrorBoundaryPathReset from "~/hooks/useErrorBoundaryPathReset";
import AdminToSettingsRedirect from "~/layout/AdminToSettingsRedirect";
import OpenPreferencesRedirect from "~/layout/OpenPreferencesRedirect";
import OpenProfileRedirect from "~/layout/OpenProfileRedirect";
// Forbidden and NotFound are statically imported — simple public pages that don't benefit from code splitting
import Forbidden from "~/pages/Forbidden";
import NotFound from "~/pages/NotFound";
import {
	CANONICAL_PORTAL_LOGIN_PATH,
	isCanonicalPortalHost,
	PORTAL_SESSION_HANDOFF_PATH
} from "~portal/helpers/canonicalPortalHost";
import classifyHost from "~portal/helpers/hostClassification";
import useCustomDomainTenantResolution from "~portal/hooks/useCustomDomainTenantResolution";
import usePortalPathTenantResolution from "~portal/hooks/usePortalPathTenantResolution";
import useVanityTenantResolution from "~portal/hooks/useVanityTenantResolution";
import PortalHostContext from "~portal/PortalHostContext";
import PortalPathTenantContext from "~portal/PortalPathTenantContext";

const MobileAvailability = React.lazy(() => import("~mobile/availability"));
const MobileEngage = React.lazy(() => import("~mobile/engage"));
const MobileFernie = React.lazy(() => import("~mobile/fernie"));
const MobileLocationExplorerDemo = React.lazy(() => import("~mobile/location-explorer-demo"));
const MobileMaterial = React.lazy(() => import("~mobile/material"));

const Count = React.lazy(() => import("~count/"));
const Forecast = React.lazy(() => import("~forecast"));
const Fulfill = React.lazy(() => import("~fulfill/"));
const Restock = React.lazy(() => import("~restock/"));
const Worksuite = React.lazy(() => import("~/pages"));
const Events = React.lazy(() => import("~mobile/events"));
const Intelligence = React.lazy(() => import("~intelligence"));
const Benchmark = React.lazy(() => import("~benchmark"));
const MyProposal = React.lazy(() => import("~/pages/myProposal"));
const Notifications = React.lazy(() => import("~/pages/notifications"));
const NotificationRedirect = React.lazy(() => import("~/pages/notifications/NotificationRedirect"));
const M365AdminConsent = React.lazy(() => import("~/pages/M365AdminConsent"));
const M365MailCallback = React.lazy(() => import("~/pages/M365MailCallback"));
const OAuthRedirect = React.lazy(() => import("~/pages/OAuthRedirect"));
const PortalTenantRouter = React.lazy(() => import("~portal/PortalTenantRouter"));
const PortalRegister = React.lazy(() => import("~portal/register"));
const PortalLanding = React.lazy(() => import("~portal/PortalLanding"));
const PortalCanonicalLogin = React.lazy(() => import("~portal/canonicalLogin"));
const PortalSessionHandoff = React.lazy(() => import("~portal/sessionHandoff"));
const Produce = React.lazy(() => import("~produce"));
const Register = React.lazy(() => import("~/pages/register"));
const Reports = React.lazy(() => import("~reports"));
const Admin = React.lazy(() => import("~admin"));
const StartTrial = React.lazy(() => import("~/pages/start-trial"));
const UnsubscribeWhatsNew = React.lazy(() => import("~/pages/unsubscribe-whats-new"));
const Verify = React.lazy(() => import("~mobile/verify"));

const LazyFallback = <CircularProgress sx={{ display: "block", m: "2rem auto" }} />;

const CUSTOMER_INVITATION_ROUTE = /^\/register\/[^/]+\/?$/;

const CanonicalPortalHostRoutes = () => (
	<Routes>
		<Route
			element={
				<Suspense fallback={LazyFallback}>
					<PortalCanonicalLogin />
				</Suspense>
			}
			path={CANONICAL_PORTAL_LOGIN_PATH}
		/>
		<Route
			element={
				<Suspense fallback={LazyFallback}>
					<OAuthRedirect />
				</Suspense>
			}
			path="/oauth-redirect"
		/>
		<Route element={<NotFound />} path="*" />
	</Routes>
);

const PortalHostRoutes = ({ tenantId }: { tenantId: string }) => (
	<PortalHostContext.Provider value={{ tenantId }}>
		<Routes>
			<Route
				element={
					<Suspense fallback={LazyFallback}>
						<PortalRegister />
					</Suspense>
				}
				path="/register"
			/>
			<Route
				element={
					<Suspense fallback={LazyFallback}>
						<PortalSessionHandoff />
					</Suspense>
				}
				path={PORTAL_SESSION_HANDOFF_PATH}
			/>
			<Route
				element={
					<Suspense fallback={LazyFallback}>
						<OAuthRedirect />
					</Suspense>
				}
				path="/oauth-redirect"
			/>
			<Route
				element={
					<Suspense fallback={LazyFallback}>
						<PortalLanding />
					</Suspense>
				}
				path="/"
			/>
			<Route
				element={
					<RequireAuth>
						<RequireSite>
							<Suspense fallback={LazyFallback}>
								<PortalTenantRouter />
							</Suspense>
						</RequireSite>
					</RequireAuth>
				}
				path="/*"
			/>
		</Routes>
	</PortalHostContext.Provider>
);

const VanityTenantResolver = ({ slug }: { slug: string }) => {
	const resolution = useVanityTenantResolution(slug);

	if (resolution.status === "loading") return LazyFallback;
	if (resolution.status === "error") {
		return (
			<RetryableErrorState
				message="We couldn't reach the server. Check your connection and try again."
				onRetry={resolution.retry}
				title="Portal unavailable"
			/>
		);
	}
	if (resolution.status === "notFound") return <NotFound />;
	return <PortalHostRoutes tenantId={resolution.tenantId} />;
};

const CustomDomainTenantResolver = ({ host }: { host: string }) => {
	const resolution = useCustomDomainTenantResolution(host);

	if (resolution.status === "loading") return LazyFallback;
	if (resolution.status === "error") {
		return (
			<RetryableErrorState
				message="We couldn't reach the server. Check your connection and try again."
				onRetry={resolution.retry}
				title="Portal unavailable"
			/>
		);
	}
	if (resolution.status === "notFound") return <NotFound />;
	return <PortalHostRoutes tenantId={resolution.tenantId} />;
};

const PortalPathTenantResolver = ({ children }: { children: React.ReactNode }) => {
	const { tenantSlug: paramSlug = "" } = useParams<{ tenantSlug: string }>();
	const resolution = usePortalPathTenantResolution(paramSlug);

	if (resolution.status === "loading") return LazyFallback;
	return <PortalPathTenantContext.Provider value={resolution.tenantId}>{children}</PortalPathTenantContext.Provider>;
};

const FulfillRouteBoundary = ({ children }: { children: React.JSX.Element }) => {
	const { t } = useTranslation("common");
	const { pathname } = useLocation();
	const errorReset = useErrorBoundaryPathReset(pathname);

	return (
		<SentryErrorBoundary
			key={errorReset.key}
			onError={errorReset.onError}
			onReset={errorReset.onReset}
			pageTitle={t("nav.fulfill.sections.fulfill.label")}
			showDialog={false}
		>
			{children}
		</SentryErrorBoundary>
	);
};

const Router = () => {
	const { isCustomer, wsapiTenant, wsUserType } = useContext(AuthContext);
	const portalAuth = usePortalAuth();
	const portalDefaultTenant = portalAuth.isCustomer ? portalAuth.tenantId : wsapiTenant || "";
	const hostClassification = useMemo(() => classifyHost(window.location.host), []);

	// Unknown user type — block access entirely
	if (wsUserType !== "" && wsUserType !== "tenant" && wsUserType !== "customer") {
		return <Navigate replace={true} to="/Forbidden" />;
	}

	if (hostClassification.kind === "reserved") {
		if (isCanonicalPortalHost(window.location.host)) {
			return <CanonicalPortalHostRoutes />;
		}
		return <NotFound />;
	}
	if (hostClassification.kind === "vanityTenant") {
		return <VanityTenantResolver slug={hostClassification.tenantSlug} />;
	}
	if (hostClassification.kind === "customDomainCandidate") {
		return <CustomDomainTenantResolver host={hostClassification.host} />;
	}

	if (
		isCustomer &&
		!window.location.pathname.startsWith("/portal") &&
		!window.location.pathname.startsWith("/oauth-redirect") &&
		!CUSTOMER_INVITATION_ROUTE.test(window.location.pathname) &&
		!isWhatsNewUnsubscribePath(window.location.pathname)
	) {
		const target = portalDefaultTenant ? `/portal/${portalDefaultTenant}/dashboard` : "/portal";
		return <Navigate replace={true} to={target} />;
	}

	return (
		<Routes>
			<Route
				element={
					<PortalPathTenantResolver>
						<Suspense fallback={LazyFallback}>
							<PortalRegister />
						</Suspense>
					</PortalPathTenantResolver>
				}
				path="/portal/:tenantSlug/register"
			/>
			<Route
				element={
					<PortalPathTenantResolver>
						<Suspense fallback={LazyFallback}>
							<PortalLanding />
						</Suspense>
					</PortalPathTenantResolver>
				}
				path="/portal/:tenantSlug"
			/>
			<Route
				element={
					<PortalPathTenantResolver>
						<RequireAuth>
							<RequireSite>
								<Suspense fallback={LazyFallback}>
									<PortalTenantRouter />
								</Suspense>
							</RequireSite>
						</RequireAuth>
					</PortalPathTenantResolver>
				}
				path="/portal/:tenantSlug/*"
			/>
			<Route
				element={
					<RequireAuth>
						<RequireSite>
							{portalDefaultTenant ? (
								<Navigate replace={true} to={`/portal/${portalDefaultTenant}/dashboard`} />
							) : (
								<Alert severity="error" sx={{ margin: "1em" }}>
									Unable to determine portal tenant.
								</Alert>
							)}
						</RequireSite>
					</RequireAuth>
				}
				path="/portal"
			/>
			<Route
				element={
					<Suspense fallback={LazyFallback}>
						<OAuthRedirect />
					</Suspense>
				}
				path="/oauth-redirect"
			/>
			<Route
				element={
					<RequireAuth>
						<RequireSite>
							<Suspense fallback={LazyFallback}>
								<M365MailCallback />
							</Suspense>
						</RequireSite>
					</RequireAuth>
				}
				path="/m365-mail-callback"
			/>
			<Route
				element={
					<Suspense fallback={LazyFallback}>
						<M365AdminConsent />
					</Suspense>
				}
				path="/m365-admin-consent"
			/>
			<Route
				element={
					<Suspense fallback={LazyFallback}>
						<Register />
					</Suspense>
				}
				path="/register/:inviteCode"
			/>
			<Route
				element={
					<Suspense fallback={LazyFallback}>
						<StartTrial />
					</Suspense>
				}
				path="/start-trial"
			/>
			<Route
				element={
					<Suspense fallback={LazyFallback}>
						<UnsubscribeWhatsNew />
					</Suspense>
				}
				path={WHATS_NEW_UNSUBSCRIBE_PATH}
			/>
			<Route element={<Forbidden />} path="/Forbidden" />
			<Route
				element={
					<RequireAuth>
						<RequireSite>
							<OpenProfileRedirect />
						</RequireSite>
					</RequireAuth>
				}
				path="/settings/profile"
			/>
			<Route
				element={
					<RequireAuth>
						<RequireSite>
							<OpenPreferencesRedirect tab="preferences" />
						</RequireSite>
					</RequireAuth>
				}
				path="/settings/preferences"
			/>
			<Route
				element={
					<RequireAuth>
						<RequireSite>
							<OpenPreferencesRedirect tab="shortcuts" />
						</RequireSite>
					</RequireAuth>
				}
				path="/settings/shortcuts"
			/>
			<Route
				element={
					<RequireAuth>
						<RequireSite>
							<OpenPreferencesRedirect tab="notifications" />
						</RequireSite>
					</RequireAuth>
				}
				path="/settings/notifications"
			/>
			<Route
				element={
					<RequireAuth>
						<RequireSite>
							<OpenPreferencesRedirect tab="app-installs" />
						</RequireSite>
					</RequireAuth>
				}
				path="/settings/app-installs"
			/>
			<Route
				element={
					<RequireAuth>
						<RequireSite>
							<OpenPreferencesRedirect tab="error-log" />
						</RequireSite>
					</RequireAuth>
				}
				path="/settings/error-log"
			/>
			<Route
				element={
					<RequireAuth>
						<RequireSite>
							<Suspense fallback={LazyFallback}>
								<Admin />
							</Suspense>
						</RequireSite>
					</RequireAuth>
				}
				path="/settings/*"
			/>
			<Route
				element={
					<RequireAuth>
						<RequireSite>
							<OpenPreferencesRedirect tab="error-log" />
						</RequireSite>
					</RequireAuth>
				}
				path="/errorLog"
			/>
			<Route
				element={
					<RequireAuth>
						<RequireSite>
							<OpenProfileRedirect />
						</RequireSite>
					</RequireAuth>
				}
				path="/account"
			/>
			<Route element={<AdminToSettingsRedirect />} path="/admin/*" />
			<Route
				element={
					<RequireAuth>
						<RequireSite>
							<OpenPreferencesRedirect tab="my-commissions" />
						</RequireSite>
					</RequireAuth>
				}
				path="/my-commissions"
			/>
			<Route
				element={
					<RequireAuth>
						<RequireSite>
							<Suspense fallback={LazyFallback}>
								<Reports />
							</Suspense>
						</RequireSite>
					</RequireAuth>
				}
				path="/reports/*"
			/>
			<Route
				element={<Navigate replace={true} to="/settings/data-management" />}
				path="/administration/data-management"
			/>
			<Route element={<Navigate replace={true} to="/settings/integrations" />} path="/integrations/*" />
			<Route
				element={
					<RequireAuth>
						<RequireSite>
							<Suspense fallback={LazyFallback}>
								<Count />
							</Suspense>
						</RequireSite>
					</RequireAuth>
				}
				path="/count/*"
			/>
			<Route
				element={
					<RequireAuth>
						<RequireSite>
							<Suspense fallback={LazyFallback}>
								<Forecast />
							</Suspense>
						</RequireSite>
					</RequireAuth>
				}
				path="/forecast/*"
			/>
			<Route element={<Navigate replace={true} to="/settings/users" />} path="/fulfill/portal/users/*" />
			<Route
				element={
					<RequireAuth>
						<RequireSite>
							<FulfillRouteBoundary>
								<Suspense fallback={LazyFallback}>
									<Fulfill />
								</Suspense>
							</FulfillRouteBoundary>
						</RequireSite>
					</RequireAuth>
				}
				path="/fulfill/*"
			/>
			<Route
				element={
					<RequireAuth>
						<RequireSite>
							<Suspense fallback={LazyFallback}>
								<MobileMaterial />
							</Suspense>
						</RequireSite>
					</RequireAuth>
				}
				path="/mobile/material/*"
			/>
			<Route
				element={
					<RequireAuth>
						<RequireSite>
							<Suspense fallback={LazyFallback}>
								<MobileMaterial />
							</Suspense>
						</RequireSite>
					</RequireAuth>
				}
				path="/material/*"
			/>
			<Route
				element={
					<RequireAuth>
						<RequireSite>
							<Suspense fallback={LazyFallback}>
								<Produce />
							</Suspense>
						</RequireSite>
					</RequireAuth>
				}
				path="/produce/*"
			/>
			<Route
				element={
					<RequireAuth>
						<RequireSite>
							<Suspense fallback={LazyFallback}>
								<Restock />
							</Suspense>
						</RequireSite>
					</RequireAuth>
				}
				path="/restock/*"
			/>
			<Route
				element={
					<RequireAuth>
						<RequireSite>
							<Suspense fallback={LazyFallback}>
								<Intelligence />
							</Suspense>
						</RequireSite>
					</RequireAuth>
				}
				path="/intelligence/*"
			/>
			<Route
				element={
					<RequireAuth>
						<RequireSite>
							<Suspense fallback={LazyFallback}>
								<Benchmark />
							</Suspense>
						</RequireSite>
					</RequireAuth>
				}
				path="/benchmark/*"
			/>
			<Route
				element={
					<RequireAuth>
						<RequireSite>
							<Suspense fallback={LazyFallback}>
								<MobileAvailability />
							</Suspense>
						</RequireSite>
					</RequireAuth>
				}
				path="/mobile/availability/*"
			/>
			<Route
				element={
					<RequireAuth>
						<RequireSite>
							<Suspense fallback={LazyFallback}>
								<MobileAvailability />
							</Suspense>
						</RequireSite>
					</RequireAuth>
				}
				path="/availability/*"
			/>
			<Route element={<Navigate replace={true} to="/mobile/availability/shrink" />} path="/mobile/shrink/*" />
			<Route element={<Navigate replace={true} to="/availability/shrink" />} path="/shrink/*" />
			<Route
				element={
					<RequireAuth>
						<RequireSite>
							<Suspense fallback={LazyFallback}>
								<MobileEngage />
							</Suspense>
						</RequireSite>
					</RequireAuth>
				}
				path="/mobile/engage/*"
			/>
			<Route
				element={
					<RequireAuth>
						<RequireSite>
							<Suspense fallback={LazyFallback}>
								<MobileEngage />
							</Suspense>
						</RequireSite>
					</RequireAuth>
				}
				path="/engage/*"
			/>
			{import.meta.env.DEV && (
				<Route
					element={
						<RequireAuth>
							<RequireSite>
								<Suspense fallback={LazyFallback}>
									<MobileLocationExplorerDemo />
								</Suspense>
							</RequireSite>
						</RequireAuth>
					}
					path="/mobile/location-explorer-demo"
				/>
			)}
			<Route
				element={
					<RequireAuth>
						<RequireSite>
							<Suspense fallback={LazyFallback}>
								<Verify />
							</Suspense>
						</RequireSite>
					</RequireAuth>
				}
				path="/verify/*"
			/>
			<Route
				element={
					<RequireAuth>
						<RequireSite>
							<Suspense fallback={LazyFallback}>
								<Events />
							</Suspense>
						</RequireSite>
					</RequireAuth>
				}
				path="/events/*"
			/>
			<Route
				element={
					<RequireAuth>
						<RequireSite>
							<Suspense fallback={LazyFallback}>
								<MobileFernie />
							</Suspense>
						</RequireSite>
					</RequireAuth>
				}
				path="/fernie"
			/>
			<Route
				element={
					<RequireAuth>
						<RequireSite>
							<Suspense fallback={LazyFallback}>
								<MobileFernie />
							</Suspense>
						</RequireSite>
					</RequireAuth>
				}
				path="/mobile/fernie"
			/>
			<Route
				element={
					<RequireAuth>
						<RequireSite>
							<Suspense fallback={LazyFallback}>
								<MyProposal />
							</Suspense>
						</RequireSite>
					</RequireAuth>
				}
				path="/my-proposal"
			/>
			<Route
				element={
					<RequireAuth>
						<RequireSite>
							<StandaloneLaunchGate>
								<Suspense fallback={LazyFallback}>
									<Worksuite />
								</Suspense>
							</StandaloneLaunchGate>
						</RequireSite>
					</RequireAuth>
				}
				path="/"
			/>
			<Route
				element={
					<RequireAuth>
						<RequireSite>
							<Suspense fallback={LazyFallback}>
								<Notifications />
							</Suspense>
						</RequireSite>
					</RequireAuth>
				}
				path="/notifications"
			/>
			<Route
				element={
					<RequireAuth>
						<RequireSite>
							<Suspense fallback={LazyFallback}>
								<NotificationRedirect />
							</Suspense>
						</RequireSite>
					</RequireAuth>
				}
				path="/notifications/:id"
			/>
			<Route element={<NotFound />} path="*" />
		</Routes>
	);
};

export default Router;
